/* "We Funded You" renewal follow-up email for funded deals.
   Same visual language as the funding-package email (solid navy header —
   Gmail strips CSS gradients — gold title, rep signature block). */

import { money, properName, fmtPhone, type RepSignature } from "./offerPackage";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export interface FundedDealInfo {
  firstName: string;
  amount: number;
  dateFunded: Date;
  funderName: string;
}

export interface StatementRequest {
  /** Full statement months to request, oldest first (e.g. ["June", "July"]). */
  months: string[];
  /** Current month name for the month-to-date bullet. */
  mtdMonth: string;
}

/**
 * Which statements to request for a renewal:
 * everything from the month BEFORE funding (its statement may not have
 * existed yet when the deal was funded) through the last completed month,
 * capped at the standard 4 full months, plus current month-to-date.
 * A deal funded this month still requests last month's statement.
 */
export function statementRequestFor(dateFunded: Date, now: Date = new Date()): StatementRequest {
  const monthIndex = (d: Date) => d.getFullYear() * 12 + d.getMonth();
  const current = monthIndex(now);
  const lastFull = current - 1;
  const earliestStandard = current - 4;
  const fundedPrev = monthIndex(dateFunded) - 1;
  const start = Math.min(Math.max(fundedPrev, earliestStandard), lastFull);
  const months: string[] = [];
  for (let m = start; m <= lastFull; m++) {
    const name = MONTH_NAMES[((m % 12) + 12) % 12];
    const year = Math.floor(m / 12);
    months.push(year !== now.getFullYear() ? `${name} ${year}` : name);
  }
  return { months, mtdMonth: MONTH_NAMES[now.getMonth()] };
}

function listWords(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function fmtDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()}`;
}

const esc = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

export function fundedFollowupSubject(firstName: string): string {
  const name = properName(firstName).trim();
  return name ? `We Funded You ${name}` : "We Funded You";
}

export function fundedFollowupHtml(deal: FundedDealInfo, rep: RepSignature | null, now: Date = new Date()): string {
  const { months, mtdMonth } = statementRequestFor(deal.dateFunded, now);
  const name = esc(properName(deal.firstName) || "there");
  const funder = esc(properName(deal.funderName));
  const sigName = rep ? esc(properName(rep.name)) : "Skyline Capital";
  const sigPhone = rep && rep.phone ? fmtPhone(rep.phone) : "(866) 960-5916";
  const sigEmail = rep && rep.email ? rep.email : "Info@skylinecaps.com";
  const monthsLabel = `${months.length} month${months.length === 1 ? "" : "s"} of statements`;
  return (
    '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;max-width:640px;margin:0 auto;">' +
    '<tr><td bgcolor="#0b2b60" style="background-color:#0b2b60;color:#f9b51b;padding:20px 32px;font-family:Arial,Helvetica,sans-serif;font-size:20px;font-weight:bold;border-radius:8px 8px 0 0;">' +
    "We Funded You &mdash; Let&#39;s Keep Growing" +
    '<div style="color:#ffffff;font-size:12px;font-weight:normal;margin-top:4px;">Renewals &amp; additional capital &mdash; fast, flexible, and transparent.</div></td></tr>' +
    '<tr><td style="padding:24px 32px;border:1px solid #e6e8ec;border-top:none;border-radius:0 0 8px 8px;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.6;color:#1a1a1a;">' +
    '<p style="margin-top:0;">Hi <b>' + name + "</b>,</p>" +
    "<p>Hope you and your business are doing well. We funded you <strong>" + money(deal.amount) +
    "</strong> on <strong>" + fmtDate(deal.dateFunded) + "</strong> with <strong>" + funder + "</strong>.</p>" +
    "<p>Since you&#39;re an existing client with payment history, we can get you funded quickly with more capital at any time.</p>" +
    '<p style="color:#0b2b60;font-weight:bold;font-size:15px;margin-top:22px;">To generate your updated agreement with the best terms, please send:</p>' +
    '<ul style="padding-left:20px;margin-top:8px;">' +
    '<li style="margin-bottom:6px;"><strong>' + monthsLabel + "</strong> &mdash; " + esc(listWords(months)) + "</li>" +
    '<li style="margin-bottom:6px;"><strong>' + esc(mtdMonth) + "</strong> month-to-date</li></ul>" +
    "<p>Once I receive them, I&#39;ll review and send your updated agreement right away.</p>" +
    '<p style="margin-bottom:0;"><b>How is business going now?</b></p>' +
    '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top:24px;border-collapse:collapse;"><tr><td style="padding-top:16px;border-top:2px solid #0b2b60;font-family:Arial,Helvetica,sans-serif;">' +
    '<div style="font-weight:bold;color:#0b2b60;font-size:16px;">' + sigName + "</div>" +
    '<div style="color:#3a3f47;font-size:13.5px;margin-top:2px;">Skyline Capital</div>' +
    '<div style="color:#3a3f47;font-size:13.5px;margin-top:4px;">' + sigPhone + "</div>" +
    '<div style="font-size:13.5px;margin-top:2px;"><a href="mailto:' + sigEmail + '" style="color:#0b2b60;text-decoration:none;">' + sigEmail + "</a></div>" +
    "</td></tr></table>" +
    "</td></tr></table>"
  );
}
